import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useStore } from '../../../store';
import styles from '../../../components/ProjectSecurity.module.css';
import layoutStyles from '../../../components/ProjectsView.module.css';
import { waitForAck } from '../../../utils/waitForAck';
import { send, waitForReady } from '../../../ws';
import { ConfirmDialog } from '../../../components/ConfirmDialog';

export const Route = createFileRoute('/projects/$projectId/settings')({
  component: ProjectSettingsTab,
});

function ProjectSettingsTab() {
  const { projectId } = useParams({ from: '/projects/$projectId/settings' });
  const project = useStore(s => s.projects[projectId]);
  const navigate = useNavigate();

  const [name, setName] = useState(project?.name ?? '');
  const [description, setDescription] = useState(project?.description ?? '');
  const [isSaving, setIsSaving] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  if (!project) return null;

  const hasChanges =
    name !== (project.name ?? '') ||
    description !== (project.description ?? '');

  const handleSave = async () => {
    if (!name.trim()) {
      setStatusMessage('Project name cannot be empty.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');

    try {
      await waitForReady();
      const corr = uuidv4();
      send('project.update', {
        id: projectId,
        name: name.trim(),
        description: description.trim() || null,
        rowVersion: project.rowVersion ?? 0,
      }, corr);

      await waitForAck<{ ok: boolean; rowVersion: number }>(corr);
      setStatusMessage('Project updated successfully.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to update project');
    } finally {
      setIsSaving(false);
    }
  };

  const handleToggleActive = async () => {
    setIsSaving(true);
    setStatusMessage('');

    try {
      await waitForReady();
      const corr = uuidv4();
      send('project.update', {
        id: projectId,
        active: !project.active,
        rowVersion: project.rowVersion ?? 0,
      }, corr);

      await waitForAck<{ ok: boolean; rowVersion: number }>(corr);
      setStatusMessage(project.active ? 'Project deactivated.' : 'Project activated.');
    } catch (error: any) {
      setStatusMessage(error?.message || 'Failed to update project status');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = () => {
    send('project.delete', { projectId });
    setShowDeleteConfirm(false);
    navigate({ to: '/projects' });
  };

  return (
    <div className={styles.wrapper}>
      {/* General */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>General</div>
        </div>

        <div className={styles.settingsFields}>
          <div className={styles.fieldGroup} style={{ marginTop: 0 }}>
            <label className={styles.label} htmlFor="project-name">
              Name
            </label>
            <input
              id="project-name"
              type="text"
              className={styles.input}
              value={name}
              onChange={e => setName(e.target.value)}
            />
          </div>

          <div className={styles.fieldGroup} style={{ marginTop: 0 }}>
            <label className={styles.label} htmlFor="project-description">
              Description
            </label>
            <textarea
              id="project-description"
              className={styles.textarea}
              value={description}
              onChange={e => setDescription(e.target.value)}
              rows={2}
              placeholder="Optional"
            />
          </div>

          <div className={styles.actions} style={{ marginTop: 0 }}>
            <button
              type="button"
              className={styles.button}
              onClick={handleSave}
              disabled={isSaving || !hasChanges || !name.trim()}
            >
              {isSaving ? 'Saving\u2026' : 'Save'}
            </button>
          </div>
        </div>
      </div>

      {/* Status */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>Status</div>
        </div>

        <div className={styles.settingsRow}>
          <div className={styles.settingsRowInfo}>
            <span className={styles.settingsRowLabel}>
              {project.active ? 'Active' : 'Inactive'}
            </span>
            <span className={styles.settingsRowHint}>
              {project.active
                ? 'Accepting agent connections.'
                : 'Not accepting new connections.'}
            </span>
          </div>
          <button
            type="button"
            className={styles.keySectionAdd}
            onClick={handleToggleActive}
            disabled={isSaving}
          >
            {project.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      </div>

      {/* Delete */}
      <div className={styles.keySection}>
        <div className={styles.keySectionHeader}>
          <div className={styles.keySectionTitle}>Delete</div>
        </div>

        <div className={styles.settingsRow}>
          <span className={styles.settingsRowHint}>
            Permanently delete this project, its applications, versions, and profiles.
          </span>
          <button
            type="button"
            className={`${styles.keyLink} ${styles.keyLinkDanger}`}
            onClick={() => setShowDeleteConfirm(true)}
            disabled={isSaving}
          >
            Delete project
          </button>
        </div>
      </div>

      {statusMessage && (
        <div className={layoutStyles.errorMessage}>
          <span className={layoutStyles.errorText}>{statusMessage}</span>
          <button
            type="button"
            onClick={() => setStatusMessage('')}
            className={layoutStyles.errorClose}
            aria-label="Dismiss status"
          >
            ×
          </button>
        </div>
      )}

      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Delete Project"
        message={`This will permanently delete "${project.name}" and all associated applications, versions, and profiles. This action cannot be undone.`}
        confirmText="Delete Project"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setShowDeleteConfirm(false)}
        requireConfirmation={true}
        confirmationPhrase={project.name}
      />
    </div>
  );
}
